import React from 'react';
import { CustomStepProps } from '../../types/survey';

export const BodyWeightAssessmentResults: React.FC<CustomStepProps> = ({
  onNext,
  onBack,
  surveyData,
}) => {
  // Calculate body weight assessment score based on user's answer
  const calculateBodyWeightScore = () => {
    const answers = surveyData?.answers || {};
    const bodyWeightRating = answers.body_weight_rating;
    
    if (bodyWeightRating === 1) return 2; // Gain weight - 2 points
    if (bodyWeightRating === 2) return 0; // Maintain weight - 0 points
    if (bodyWeightRating === 3) return 1; // Lose weight - 1 point
    
    return 0; // Default
  };

  const score = calculateBodyWeightScore();

  const getRecommendation = () => {
    if (score === 2) {
      return {
        title: "Weight Gain Guidance",
        text: "Being underweight can have dangerous consequences for your health, so it's a good idea to find out what's causing it. Consult our specialists to check the proportions of your body composition and create a nutrition plan that will help you achieve your ideal body shape and maintain it for years to come."
      };
    } else if (score === 1) {
      return {
        title: "Smart Weight Loss",
        text: "If you want to lose weight, do so smartly. An improper weight-loss diet can cause numerous disorders, so do not trust miracle cures from the Internet or newspapers. Moreover, a bad diet is likely to result in weight loss failure, which can reduce motivation and self-esteem. Would you like to know how to lose weight effectively? Consult our specialists, who will establish an individual nutrition plan for you and help you permanently change your eating habits for the better."
      };
    } else {
      return {
        title: "Maintaining Success",
        text: "Are you maintaining a steady weight? Well done! Apparently, you have adopted the right genetic predispositions from your parents, and you lead a healthy lifestyle that epigenetically supports your body shape. Consult our specialists to check the proportions of your body composition and create a nutrition plan that will help you maintain your ideal body shape for years to come."
      };
    }
  };

  const recommendation = getRecommendation();

  const getGoalText = () => {
    const bodyWeightRating = surveyData?.answers?.body_weight_rating;
    if (bodyWeightRating === 1) return "Gain Weight";
    if (bodyWeightRating === 2) return "Maintain Weight";
    if (bodyWeightRating === 3) return "Lose Weight";
    return "Not specified";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-royal-blue-lightest via-white to-royal-gold-lightest flex items-center justify-center p-4">
      <div className="max-w-3xl w-full">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header Section with Gradient */}
          <div className="bg-gradient-to-r from-royal-gold to-royal-blue-lightest p-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-white/30 rounded-full mb-4">
                <svg className="w-10 h-10 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                    d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                </svg>
              </div>
              <h1 className="text-4xl font-bold mb-4 text-gray-800">
                Body Shape & Weight Assessment
              </h1>
              <p className="text-xl text-gray-700">
                Your personalized body composition analysis
              </p>
            </div>
          </div>

          {/* Content Section */}
          <div className="p-8">
            {/* Current Goal Display */}
            <div className="bg-gradient-to-r from-royal-gold to-royal-blue-lightest rounded-2xl p-6 mb-8">
              <div className="text-center">
                <div className="text-lg text-gray-700 mb-2">Your Goal</div>
                <div className="text-3xl font-bold text-gray-800">{getGoalText()}</div>
              </div>
            </div>

            {/* Summary Section */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                {recommendation.title}
              </h2>
              <div className={`${score === 0 ? 'bg-green-50' : score === 1 ? 'bg-blue-50' : 'bg-amber-50'} rounded-lg p-6`}>
                <p className="text-gray-700 leading-relaxed">
                  {recommendation.text}
                </p>
              </div>
            </div>

            {/* Additional Insights */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                Our Experts Recommend
              </h2>
              <div className="bg-gradient-to-r from-royal-gold to-royal-blue-lightest rounded-lg p-6">
                <div className="space-y-4">
                  <div className="flex items-start">
                    <span className="text-royal-gold mr-3 text-xl">•</span>
                    <p className="text-gray-700">
                      Body composition analysis provides more accurate insights than weight alone
                    </p>
                  </div>
                  <div className="flex items-start">
                    <span className="text-royal-gold mr-3 text-xl">•</span>
                    <p className="text-gray-700">
                      Sustainable changes require personalized nutrition and exercise plans
                    </p>
                  </div>
                  <div className="flex items-start">
                    <span className="text-royal-gold mr-3 text-xl">•</span>
                    <p className="text-gray-700">
                      Regular monitoring helps track progress and adjust strategies
                    </p>
                  </div>
                  <div className="flex items-start">
                    <span className="text-royal-gold mr-3 text-xl">•</span>
                    <p className="text-gray-700">
                      Professional guidance ensures safe and effective results
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Key Metrics */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Body Composition Metrics</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-royal-gold-lightest rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-semibold text-gray-900">BMI Analysis</span>
                      <p className="text-xs text-gray-600 mt-1">Body Mass Index calculation</p>
                    </div>
                    <svg className="w-8 h-8 text-royal-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                </div>
                <div className="bg-royal-blue-lightest rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-semibold text-gray-900">Body Fat %</span>
                      <p className="text-xs text-gray-600 mt-1">Essential vs stored fat ratio</p>
                    </div>
                    <svg className="w-8 h-8 text-royal-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                  </div>
                </div>
                <div className="bg-royal-gold-lightest rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-semibold text-gray-900">Muscle Mass</span>
                      <p className="text-xs text-gray-600 mt-1">Lean muscle composition</p>
                    </div>
                    <svg className="w-8 h-8 text-royal-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                </div>
                <div className="bg-royal-blue-lightest rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-semibold text-gray-900">Metabolic Rate</span>
                      <p className="text-xs text-gray-600 mt-1">Daily calorie requirements</p>
                    </div>
                    <svg className="w-8 h-8 text-royal-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center mt-8">
              <button
                onClick={onBack}
                className="px-8 py-3 bg-white border-2 border-royal-gold text-royal-gold font-semibold rounded-lg hover:bg-royal-gold hover:text-white transition-colors"
              >
                Previous
              </button>
              <button
                onClick={onNext}
                className="px-8 py-3 bg-royal-gold text-white font-semibold rounded-lg hover:bg-royal-gold-dark transition-colors"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};